/**
 * Shared atoms for super-admin screens — keeps each page lean.
 */
import { useEffect, useRef, useState } from "react";

export const card = {
  background: "var(--bg-surface, #E4E2DE)",
  border: "1px solid var(--border, rgba(12,12,11,0.09))",
  borderRadius: 16,
  padding: 20,
};

export const inputStyle = {
  background: "var(--bg-inset, #D8D6D1)",
  border: "1px solid var(--border, rgba(12,12,11,0.09))",
  borderRadius: 12,
  height: 44,
  padding: "0 14px",
  fontFamily: "Barlow, sans-serif",
  fontSize: 14,
  color: "var(--text-primary, #0C0C0B)",
  outline: "none",
  width: "100%",
};

export const fieldLabel = {
  fontFamily: "Barlow, sans-serif",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--text-tertiary, #8A8884)",
  marginBottom: 6,
  display: "block",
};

export const PrimaryBtn = ({ children, ...rest }) => (
  <button
    {...rest}
    style={{
      background: "var(--accent, #2A2825)",
      color: "var(--bg-base, #ECEAE6)",
      borderRadius: 100,
      height: 40,
      padding: "0 20px",
      fontFamily: "Barlow, sans-serif",
      fontWeight: 700,
      fontSize: 14,
      ...rest.style,
    }}
  >
    {children}
  </button>
);

export const GhostBtn = ({ children, ...rest }) => (
  <button
    {...rest}
    style={{
      background: "transparent",
      border: "1px solid var(--border-strong, rgba(12,12,11,0.18))",
      color: "var(--text-primary, #0C0C0B)",
      borderRadius: 100,
      height: 40,
      padding: "0 20px",
      fontFamily: "Barlow, sans-serif",
      fontWeight: 700,
      fontSize: 14,
      ...rest.style,
    }}
  >
    {children}
  </button>
);

export const DangerBtn = ({ children, ...rest }) => (
  <button
    {...rest}
    style={{
      background: "rgba(239,68,68,0.10)",
      border: "1px solid rgba(239,68,68,0.25)",
      color: "var(--error, #EF4444)",
      borderRadius: 100,
      height: 40,
      padding: "0 20px",
      fontFamily: "Barlow, sans-serif",
      fontWeight: 700,
      fontSize: 14,
      ...rest.style,
    }}
  >
    {children}
  </button>
);

export const StatusBadge = ({ value }) => {
  const v = (value || "").toLowerCase();
  let bg = "rgba(42,40,37,0.10)", fg = "var(--text-secondary, #4A4845)";
  if (["active", "verified", "delivered", "approved", "completed", "paid"].includes(v)) {
    bg = "var(--accent, #2A2825)"; fg = "var(--bg-base, #ECEAE6)";
  } else if (["pending", "scheduled", "shipped", "confirmed"].includes(v)) {
    bg = "rgba(245,158,11,0.12)"; fg = "var(--warning, #F59E0B)";
  } else if (["banned", "suspended", "rejected", "removed", "refunded", "failed"].includes(v)) {
    bg = "rgba(239,68,68,0.12)"; fg = "var(--error, #EF4444)";
  }
  return (
    <span style={{
      background: bg, color: fg,
      padding: "3px 10px", borderRadius: 100,
      fontFamily: "Barlow", fontSize: 11, fontWeight: 700,
      textTransform: "uppercase",
    }}>
      {v || "—"}
    </span>
  );
};

export const Modal = ({ open, onClose, title, children, maxWidth = 560 }) => {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 80,
        background: "rgba(12,12,11,0.50)", backdropFilter: "blur(4px)",
        display: "grid", placeItems: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth, padding: 24,
          background: "var(--bg-surface, #E4E2DE)",
          border: "1px solid var(--border, rgba(12,12,11,0.09))",
          borderRadius: 20,
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        {title && (
          <h2 style={{
            fontFamily: "Barlow Condensed, sans-serif",
            fontSize: 22, fontWeight: 800, marginBottom: 12,
          }}>
            {title}
          </h2>
        )}
        {children}
      </div>
    </div>
  );
};

export const FilterPills = ({ value, onChange, options, testid }) => (
  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid={testid}>
    {options.map((o) => {
      const on = value === o.value;
      return (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          style={{
            background: on ? "var(--accent, #2A2825)" : "transparent",
            color: on ? "var(--bg-base, #ECEAE6)" : "var(--text-secondary, #4A4845)",
            border: on ? "none" : "1px solid var(--border-strong, rgba(12,12,11,0.18))",
            borderRadius: 100, height: 34, padding: "0 16px",
            fontFamily: "Barlow", fontSize: 13, fontWeight: on ? 700 : 600,
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

// Section card-style container
export const Card = ({ children, style }) => (
  <div style={{ ...card, ...style }}>{children}</div>
);

export const TableShell = ({ children }) => (
  <div style={{
    background: "var(--bg-surface, #E4E2DE)",
    border: "1px solid var(--border, rgba(12,12,11,0.09))",
    borderRadius: 16,
    overflow: "hidden",
  }}>
    <div className="sa-table-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      {children}
    </div>
  </div>
);

export const Th = ({ children }) => (
  <th style={{
    fontFamily: "Barlow", fontSize: 11, fontWeight: 700,
    letterSpacing: "0.07em", textTransform: "uppercase",
    color: "var(--text-tertiary, #8A8884)",
    padding: "10px 14px", textAlign: "left",
    background: "var(--bg-inset, #D8D6D1)",
    borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))",
  }}>{children}</th>
);

export const Td = ({ children, style }) => (
  <td style={{
    padding: "14px 14px",
    fontFamily: "Barlow", fontSize: 14, fontWeight: 400,
    color: "var(--text-secondary, #4A4845)",
    borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))",
    ...style,
  }}>{children}</td>
);

export function SummaryTiles({ tiles }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(160px, 1fr))`,
      gap: 12,
    }}>
      {tiles.map((t, i) => (
        <div key={i} style={card} data-testid={`tile-${(t.label || "").toLowerCase().replace(/\W+/g, "-")}`}>
          <div style={{
            fontFamily: "Barlow", fontSize: 11, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.08em",
            color: "var(--text-tertiary, #8A8884)",
          }}>
            {t.label}
          </div>
          <div style={{
            fontFamily: "Barlow Condensed, sans-serif",
            fontSize: 32, fontWeight: 900, lineHeight: 1,
            marginTop: 4, color: "var(--text-primary, #0C0C0B)",
            wordBreak: "break-word",
          }}>
            {t.value ?? "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

export function useDebouncedValue(value, ms = 300) {
  const [v, setV] = useState(value);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setV(value), ms);
    return () => clearTimeout(timer.current);
  }, [value, ms]);
  return v;
}

export function fmtINR(n) {
  if (n == null) return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}

export function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function EmptyState({ title = "No data yet", sub }) {
  return (
    <div style={{ ...card, textAlign: "center", padding: 40 }}>
      <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800, color: "var(--text-primary, #0C0C0B)" }}>{title}</div>
      {sub && <div style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export function Toolbar({ children }) {
  return (
    <div style={{ ...card, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      {children}
    </div>
  );
}
